import { Box, Flex, SimpleGrid } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo, useState } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useDeskCollectionDasboardStatistics } from "../../api/hooks/deskCollection/deskCollection/useDeskCollectionDasboardStatistics.ts";
import { useDeskCollections } from "../../api/hooks/deskCollection/deskCollection/useDeskCollections.ts";
import { useUpdateDeskCollection } from "../../api/hooks/deskCollection/deskCollection/useUpdateDeskCollection.ts";
import SingleFieldCard from "../../components/Cards/SingleFieldCard/SingleFieldCard.tsx";
import SvgCalendar from "../../components/Icons/Calendar.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import { MultiSelect } from "../../components/MultiSelect.tsx";
import Table from "../../components/Table/Table.tsx";
import Title from "../../components/Title/Title.tsx";
import {
  DeskCollectionOrderBy,
  OrderBy,
  UpdateDeskCollectionInput,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { getCompanyOptions } from "../../utils/company.ts";
import { toLocalNumber, toPercent } from "../../utils/number.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { getDeskCollectionApiSort } from "../DeskCollectionTools/utils.ts";
import { useDeskCollectionDashboardTableData } from "./hooks/useDeskCollectionDashboardTableData.tsx";
import { DeskCollectionDashboardFilterValue } from "./types.ts";
import { getDeskCollectionDashboardApiFilter } from "./utils.ts";

const DeskCollectionDashboardPage = () => {
  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<DeskCollectionDashboardFilterValue>({});
  const apiFilter = useMemo(() => {
    return getDeskCollectionDashboardApiFilter(filter);
  }, [filter]);
  const [orderBy, setOrderBy] = useState<DeskCollectionOrderBy>({
    collectionDate: OrderBy.Desc,
  });

  const [
    {
      data: deskCollectionResponse,
      error: deskCollectionError,
      fetching: fetchingDeskCollection,
    },
    refresh,
  ] = useDeskCollections({ filter: apiFilter, orderBy, page });
  const deskCollections = deskCollectionResponse?.deskCollections.data;
  const pageCount = getPagesCount(
    deskCollectionResponse?.deskCollections.total
  );

  const [
    {
      data: deskCollectionStatisticsResponse,
      error: deskCollectionStatisticsError,
      fetching: fetchingDeskCollectionStatistics,
    },
    refreshStatistics,
  ] = useDeskCollectionDasboardStatistics({ filter: apiFilter });
  const statistics = deskCollectionStatisticsResponse?.statistics;

  const error = deskCollectionError || deskCollectionStatisticsError;

  const [{ data: customerOptionsResponse, fetching: customerOptionsFetching }] =
    useCompanyOptions();
  const companyOptions = customerOptionsResponse?.customers.data;

  const [, updateDeskCollection] = useMutationNotificationWrapper(
    useUpdateDeskCollection(),
    {
      success: {
        message: "Desk Collection record has been successfully updated",
      },
    }
  );

  const handleUpdateDeskCollection = useCallback(
    (id: number, input: UpdateDeskCollectionInput) => {
      updateDeskCollection({ id, input }).then((data) => {
        if (!data.error) {
          refresh();
          refreshStatistics();
        }
      });
    },
    [updateDeskCollection, refresh, refreshStatistics]
  );

  const [columns, rows] = useDeskCollectionDashboardTableData({
    data: deskCollections,
    onInlineEdit: handleUpdateDeskCollection,
  });

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleFilterChange = useCallback(
    (key: keyof DeskCollectionDashboardFilterValue, value: unknown) => {
      setFilter((prevFilter) => {
        return { ...prevFilter, [key]: value };
      });
      setPage(1);
    },
    []
  );

  const handleOnSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getDeskCollectionApiSort(sort));
    },
    [setOrderBy]
  );

  if (error) {
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <>
      <Layout>
        <Flex direction="column" gap={{ base: 16, sm: 24 }}>
          <Title size="h1">Desk Collection Dashboard</Title>
          <SimpleGrid
            w="100%"
            cols={4}
            breakpoints={[
              { maxWidth: "sm", cols: 2 },
              { maxWidth: "lg", cols: 3 },
            ]}
            spacing={20}
            verticalSpacing={12}
          >
            <DatePickerInput
              label="Collection Date"
              placeholder="Select Collection Date"
              icon={<SvgCalendar />}
              firstDayOfWeek={0}
              type="range"
              size="m"
              clearable
              onChange={(value) => handleFilterChange("collectionDate", value)}
            />
            <MultiSelect
              size="m"
              clearable
              searchable
              label="Customer Name"
              placeholder="Select Customer Name"
              data={getCompanyOptions(companyOptions)}
              loading={customerOptionsFetching}
              onChange={(value) =>
                handleFilterChange(
                  "customerId",
                  value.length ? value.map(Number) : null
                )
              }
            />
          </SimpleGrid>
          <SimpleGrid cols={3} spacing={20} verticalSpacing={8}>
            <Box>
              <SingleFieldCard
                label="Number of Contacted Customers"
                value={toLocalNumber(statistics?.contactedCustomersCount)}
                loading={fetchingDeskCollectionStatistics}
              />
            </Box>
            <Box>
              <SingleFieldCard
                label="Number of Responses"
                value={toLocalNumber(statistics?.responsiveCustomersCount)}
                loading={fetchingDeskCollectionStatistics}
              />
            </Box>
            <Box>
              <SingleFieldCard
                label="Response Rate"
                value={toPercent(
                  statistics?.responsiveCustomersCount === 0
                    ? 0
                    : (statistics?.responsiveCustomersCount ?? 0) /
                        (statistics?.contactedCustomersCount ?? 0),
                  "decimal"
                )}
                loading={fetchingDeskCollectionStatistics}
              />
            </Box>
          </SimpleGrid>
          <Table
            columns={columns}
            rows={rows}
            verticalAlignment="top"
            loading={fetchingDeskCollection}
            pagination={{
              pageCount: pageCount,
              page: page,
              onPageChange: handlePageChange,
            }}
            onSortChange={handleOnSortChange}
          />
        </Flex>
      </Layout>
    </>
  );
};

export default DeskCollectionDashboardPage;
